'use client';

import { Flags } from '@ametista-ai/api-schema';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box, Flex, Stack } from '@mantine/core';
import { useFlag } from '@/flags/useFlag';
import { useCurrentAgency } from '@/queries/agencies/useCurrentAgency';
import { useMe } from '@/queries/me/useMe';
import { useStore } from '@/stores';
import { IconButton } from '@/ui';
import { SearchButton } from '../Search';
import { AgencyButton } from './AgencyButton';
import { AiNotificationBadge } from './AiNotificationBadge';
import { CalendarMenuItem } from './CalendarMenuItem';
import { ClientList } from './ClientList';
import { NavbarButton } from './NavbarButton';
import { NotificationBadge } from './NotificationBadge';
import { SettingsNavbar } from './SettingsNavbar';
import classes from './Navbar.module.css';

export const Navbar = observer(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const store = useStore();
  const { data: me } = useMe();
  const { data: agency } = useCurrentAgency();
  const isAiEnabled = useFlag(Flags.Ai);
  const isAiVideosEnabled = useFlag(Flags.AiVideos);

  return (
    <>
      <Box
        className={classes.navbar}
        w={store.navbar.collapsed ? 0 : store.isMobile ? '100%' : store.navbar.width}
        data-collapsed={store.navbar.collapsed ? true : undefined}
        data-mobile={store.isMobile ? true : undefined}
      >
        <Box className={classes.navbar__content}>
          {pathname.startsWith('/settings') ? (
            <SettingsNavbar />
          ) : (
            <Stack gap="xs" mah="100%">
              <Flex
                justify="space-between"
                align="center"
                direction="row"
                className={classes.navbar__header}
                gap="xs"
              >
                <AgencyButton />
                <SearchButton visibleFrom="sm" />
                <IconButton
                  iconName="chevronsLeft"
                  variant="subtle"
                  onClick={() => store.navbar.toggle()}
                  hiddenFrom="sm"
                />
              </Flex>
              <Stack gap={0}>
                <NavbarButton label={t('navbar.home')} icon="home" href="/home" />
                <NavbarButton label={t('navbar.dashboard')} icon="chartBar" href="/dashboard" />
                {/* <NavbarButton label={t('navbar.my-tasks')} icon="list" href="/tasks" /> */}
                <CalendarMenuItem />
                <NavbarButton
                  label={t('navbar.inbox')}
                  icon="bell"
                  href="/inbox"
                  exact={false}
                  rightSection={<NotificationBadge />}
                />
                {agency?.role !== 'client' && (
                  <NavbarButton
                    label={t('navbar.file-storage')}
                    icon="folder"
                    href="/file-storage"
                  />
                )}
                {isAiEnabled && (
                  <>
                    <NavbarButton
                      label={t('navbar.ai')}
                      icon="sparkles"
                      href="/ai/"
                      exact={false}
                      rightSection={<AiNotificationBadge />}
                    />
                    {isAiVideosEnabled && me?.user?.role === 'admin' && (
                      <NavbarButton label={t('navbar.ai-videos')} icon="movie" href="/ai-videos" />
                    )}
                  </>
                )}
              </Stack>
              <ClientList />
            </Stack>
          )}
        </Box>
      </Box>
      {!store.navbar.collapsed && (
        <Box className={classes.navbar__overlay} onClick={() => store.navbar.toggle()} />
      )}
    </>
  );
});
